import React, { useState, useEffect, useCallback } from 'react';
import './index.scss';

const Game1024 = () => {
  const [board, setBoard] = useState([]);
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);

  // 添加事件监听器
  useEffect(() => {
    const handleGameControl = (event) => {
      const { action, gameType } = event.detail;
      if (gameType !== 'game1024') return;
      
      switch (action) {
        case 'up':
          if (!gameOver) move('up');
          break;
        case 'left':
          if (!gameOver) move('left');
          break;
        case 'right':
          if (!gameOver) move('right');
          break;
        case 'down':
          if (!gameOver) move('down');
          break;
        case 'restart':
          restartGame();
          break;
        default:
          break;
      }
    };

    window.addEventListener('gameControl', handleGameControl);
    return () => window.removeEventListener('gameControl', handleGameControl);
  }, [gameOver]);

  // 初始化游戏板
  const initializeBoard = useCallback(() => {
    const newBoard = Array(4).fill(null).map(() => Array(4).fill(0));
    addRandomTile(newBoard);
    addRandomTile(newBoard);
    return newBoard;
  }, []);

  // 添加随机数字块
  const addRandomTile = useCallback((board) => {
    const emptyCells = [];
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        if (board[i][j] === 0) {
          emptyCells.push({ row: i, col: j });
        }
      }
    }
    
    if (emptyCells.length > 0) {
      const randomCell = emptyCells[Math.floor(Math.random() * emptyCells.length)];
      board[randomCell.row][randomCell.col] = Math.random() < 0.9 ? 2 : 4;
    }
  }, []);

  // 检查游戏是否结束
  const checkGameOver = useCallback((board) => {
    // 检查是否有空格
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        if (board[i][j] === 0) return false;
      }
    }
    
    // 检查是否可以合并
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        const current = board[i][j];
        // 检查右边
        if (j < 3 && board[i][j + 1] === current) return false;
        // 检查下边
        if (i < 3 && board[i + 1][j] === current) return false;
      }
    }
    
    return true;
  }, []);

  // 检查是否获胜
  const checkWin = useCallback((board) => {
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        if (board[i][j] === 1024) return true;
      }
    }
    return false;
  }, []);

  // 移动逻辑
  const move = useCallback((direction) => {
    if (gameOver) return;

    let moved = false;
    const newBoard = board.map(row => [...row]);
    let newScore = score;

    const moveRow = (row, direction) => {
      const filtered = row.filter(cell => cell !== 0);
      const merged = [];
      
      for (let i = 0; i < filtered.length; i++) {
        if (i < filtered.length - 1 && filtered[i] === filtered[i + 1]) {
          merged.push(filtered[i] * 2);
          newScore += filtered[i] * 2;
          i++;
        } else {
          merged.push(filtered[i]);
        }
      }
      
      while (merged.length < 4) {
        merged.push(0);
      }
      
      return merged;
    };

    if (direction === 'left' || direction === 'right') {
      for (let i = 0; i < 4; i++) {
        const row = [...newBoard[i]];
        const originalRow = [...row];
        
        if (direction === 'right') {
          row.reverse();
        }
        
        const movedRow = moveRow(row, direction);
        
        if (direction === 'right') {
          movedRow.reverse();
        }
        
        newBoard[i] = movedRow;
        
        if (JSON.stringify(originalRow) !== JSON.stringify(movedRow)) {
          moved = true;
        }
      }
    } else {
      for (let j = 0; j < 4; j++) {
        const column = [newBoard[0][j], newBoard[1][j], newBoard[2][j], newBoard[3][j]];
        const originalColumn = [...column];
        
        if (direction === 'down') {
          column.reverse();
        }
        
        const movedColumn = moveRow(column, direction);
        
        if (direction === 'down') {
          movedColumn.reverse();
        }
        
        for (let i = 0; i < 4; i++) {
          newBoard[i][j] = movedColumn[i];
        }
        
        if (JSON.stringify(originalColumn) !== JSON.stringify(movedColumn)) {
          moved = true;
        }
      }
    }

    if (moved) {
      addRandomTile(newBoard);
      setBoard(newBoard);
      setScore(newScore);
      
      if (newScore > bestScore) {
        setBestScore(newScore);
        localStorage.setItem('game1024_best_score', newScore.toString());
      }
      
      if (checkWin(newBoard) && !won) {
        setWon(true);
      }
      
      if (checkGameOver(newBoard)) {
        setGameOver(true);
      }
    }
  }, [board, score, bestScore, gameOver, won, addRandomTile, checkWin, checkGameOver]);

  // 键盘事件处理
  useEffect(() => {
    const handleKeyPress = (e) => {
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          move('up');
          break;
        case 'ArrowDown':
          e.preventDefault();
          move('down');
          break;
        case 'ArrowLeft':
          e.preventDefault();
          move('left');
          break;
        case 'ArrowRight':
          e.preventDefault();
          move('right');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [move]);

  // 重新开始游戏
  const restartGame = () => {
    setBoard(initializeBoard());
    setScore(0);
    setGameOver(false);
    setWon(false);
  };

  // 初始化游戏
  useEffect(() => {
    const savedBestScore = localStorage.getItem('game1024_best_score');
    if (savedBestScore) {
      setBestScore(parseInt(savedBestScore));
    }
    setBoard(initializeBoard());
  }, [initializeBoard]);

  // 获取方块样式类名
  const getTileClass = (value) => {
    if (value === 0) return 'tile tile-empty';
    return `tile tile-${value}`;
  };

  return (
    <div className="game-1024">
      <div className="game-header">
        <div className="game-scores">
          <div className="score-container">
            <div className="score-label">分数</div>
            <div className="score-value">{score}</div>
          </div>
          <div className="score-container">
            <div className="score-label">最高分</div>
            <div className="score-value">{bestScore}</div>
          </div>
        </div>
        
        <div className="header-controls">
          <div className="controls-row">
            <button className="restart-btn" onClick={restartGame}>
              重新开始
            </button>
            <div className="mobile-controls">
              <button className="control-btn up" onClick={() => move('up')}>↑</button>
              <div className="horizontal-controls">
                <button className="control-btn left" onClick={() => move('left')}>←</button>
                <button className="control-btn down" onClick={() => move('down')}>↓</button>
                <button className="control-btn right" onClick={() => move('right')}>→</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="game-board-container">
        <div className="game-board">
          {board.map((row, rowIndex) => (
            <div key={rowIndex} className="board-row">
              {row.map((cell, colIndex) => (
                <div key={`${rowIndex}-${colIndex}`} className={getTileClass(cell)}>
                  {cell !== 0 && cell}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {won && (
        <div className="game-overlay won">
          <div className="overlay-content">
            <h2>恭喜！</h2>
            <p>你达到了1024！</p>
            <button onClick={restartGame}>继续游戏</button>
          </div>
        </div>
      )}

      {gameOver && (
        <div className="game-overlay game-over">
          <div className="overlay-content">
            <h2>游戏结束</h2>
            <p>最终分数: {score}</p>
            <button onClick={restartGame}>重新开始</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Game1024; 